import { fg } from '@atlaskit/platform-feature-flags';
import {
	JIRA_PLANS_ADD_DEPENDENCY_KEY,
	JIRA_PLANS_ADD_PROGRAM_BOARD_KEY,
	JIRA_PLANS_CREATE_RELEASE_KEY,
	JIRA_PLANS_CREATE_WORK_ITEM_KEY,
	JIRA_PLANS_DELETE_PROGRAM_BOARD_KEY,
	JIRA_PLANS_MANAGE_FEATURES_KEY,
	JIRA_PLANS_REMOVE_DEPENDENCY_KEY,
	JIRA_PLANS_UPDATE_WORK_SOURCE_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

type AnthropicToolPayload = {
	tool_name?: string;
	input?: unknown;
};

type JiraPlansNormalizedMessageAction = MessageAction & {
	jiraPlansOriginalActionKey?: string;
};

type JiraPlansActionData = {
	isCustomUiActionKey: (actionKey: string) => boolean;
	isLegacyActionKey: (actionKey: string) => boolean;
	isAnthropicActionKey: (actionKey: string) => boolean;
	resolveAnthropicActionKey: (actionKey: string) => string | undefined;
	getCustomUiActionKey: (action: MessageAction) => string | undefined;
	normalizeCustomUiActions: (actions: MessageAction[]) => MessageAction[];
	shouldNotRenderLLMContentForAction: (action: MessageAction) => boolean;
	getPlanActionData: <T>(data: unknown) => T;
	withPlanActionData: (originalData: unknown, payload: unknown) => unknown;
	isPlanActionKeyForRenderer: (actionKey: string, legacyActionKey: string) => boolean;
};

const parseActionData = (data: unknown): unknown => {
	if (typeof data !== 'string') {
		return data;
	}

	try {
		return JSON.parse(data);
	} catch {
		return data;
	}
};

const hasObjectKeys = (data: unknown, keys: string[]): boolean =>
	data !== null && typeof data === 'object' && keys.every((key) => key in data);

const getPayloadCandidate = (data: unknown): unknown => {
	if (
		data !== null &&
		typeof data === 'object' &&
		'input' in data &&
		(data as { input?: unknown }).input !== null &&
		typeof (data as { input?: unknown }).input === 'object'
	) {
		return (data as { input: unknown }).input;
	}

	return data;
};

const getToolName = (action: MessageAction): string | undefined => {
	const data = parseActionData(action.data);

	if (data !== null && typeof data === 'object' && 'tool_name' in data) {
		const toolName = (data as { tool_name?: unknown }).tool_name;
		return typeof toolName === 'string' ? toolName : undefined;
	}
	return undefined;
};

const JIRA_PLANS_LEGACY_KEY_TO_ANTHROPIC_MIGRATION_KEY: Record<string, string> = {
	[JIRA_PLANS_CREATE_WORK_ITEM_KEY]: 'execute_is.jira_atlassian_plan_create_work_item',
	[JIRA_PLANS_CREATE_RELEASE_KEY]: 'execute_is.jira_atlassian_plan_create_release',
	[JIRA_PLANS_UPDATE_WORK_SOURCE_KEY]: 'execute_is.jira_atlassian_plan_update_work_sources',
	[JIRA_PLANS_MANAGE_FEATURES_KEY]: 'execute_is.jira_atlassian_plan_set_release_enabled',
	[JIRA_PLANS_ADD_DEPENDENCY_KEY]: 'execute_is.jira_atlassian_plan_add_work_item_dependencies',
	[JIRA_PLANS_REMOVE_DEPENDENCY_KEY]:
		'execute_is.jira_atlassian_plan_remove_work_item_dependencies',
	[JIRA_PLANS_ADD_PROGRAM_BOARD_KEY]: 'execute_is.jira_atlassian_plan_add_program_board',
	[JIRA_PLANS_DELETE_PROGRAM_BOARD_KEY]: 'execute_is.jira_atlassian_plan_delete_program_board',
};

const JIRA_PLANS_ANTHROPIC_KEY_TO_LEGACY_KEY: Record<string, string> = Object.fromEntries(
	Object.entries(JIRA_PLANS_LEGACY_KEY_TO_ANTHROPIC_MIGRATION_KEY).flatMap(
		([legacyKey, anthropicKey]) => [
			[anthropicKey, legacyKey],
			[anthropicKey.replace('execute_is.', 'is.'), legacyKey],
		],
	),
);

const JIRA_PLANS_CUSTOM_UI_LEGACY_KEYS: ReadonlySet<string> = new Set(
	Object.keys(JIRA_PLANS_LEGACY_KEY_TO_ANTHROPIC_MIGRATION_KEY),
);

const getAnthropicKeyFromLegacyKey = (legacyKey: string): string | undefined =>
	JIRA_PLANS_LEGACY_KEY_TO_ANTHROPIC_MIGRATION_KEY[legacyKey];

const resolveJiraPlansAnthropicActionKey = (actionKey: string): string | undefined =>
	JIRA_PLANS_ANTHROPIC_KEY_TO_LEGACY_KEY[actionKey];

const isJiraPlansAnthropicActionKey = (actionKey: string): boolean =>
	actionKey in JIRA_PLANS_ANTHROPIC_KEY_TO_LEGACY_KEY;

const isJiraPlansLegacyActionKey = (actionKey: string): boolean =>
	JIRA_PLANS_CUSTOM_UI_LEGACY_KEYS.has(actionKey);

const isJiraPlansCustomUiActionKey = (actionKey: string): boolean =>
	isJiraPlansLegacyActionKey(actionKey) || isJiraPlansAnthropicActionKey(actionKey);

const getJiraPlansAnthropicKeyFromPayload = (action: MessageAction): string | undefined => {
	const data = getPayloadCandidate(parseActionData(action.data));

	if (
		hasObjectKeys(data, [
			'planId',
			'programBoardId',
			'programBoardName',
			'start',
			'iterationsCount',
			'iterationLength',
		])
	) {
		return getAnthropicKeyFromLegacyKey(JIRA_PLANS_DELETE_PROGRAM_BOARD_KEY);
	}

	if (
		hasObjectKeys(data, [
			'planId',
			'planName',
			'name',
			'start',
			'iterationsCount',
			'iterationLength',
		])
	) {
		return getAnthropicKeyFromLegacyKey(JIRA_PLANS_ADD_PROGRAM_BOARD_KEY);
	}

	if (
		hasObjectKeys(data, [
			'planId',
			'scenarioId',
			'dependencyType',
			'sourceItemKey',
			'sourceItemId',
			'targetItemKey',
			'targetItemId',
		])
	) {
		return getAnthropicKeyFromLegacyKey(JIRA_PLANS_ADD_DEPENDENCY_KEY);
	}

	if (
		hasObjectKeys(data, [
			'planId',
			'scenarioId',
			'dependencyType',
			'sourceItemKey',
			'targetItemKey',
		])
	) {
		return getAnthropicKeyFromLegacyKey(JIRA_PLANS_REMOVE_DEPENDENCY_KEY);
	}

	if (
		hasObjectKeys(data, [
			'planId',
			'scenarioId',
			'projectId',
			'sourceName',
			'summary',
			'issueTypeId',
			'workType',
		])
	) {
		return getAnthropicKeyFromLegacyKey(JIRA_PLANS_CREATE_WORK_ITEM_KEY);
	}

	if (
		hasObjectKeys(data, [
			'planId',
			'scenarioId',
			'projectId',
			'projectName',
			'name',
			'start',
			'end',
		])
	) {
		return getAnthropicKeyFromLegacyKey(JIRA_PLANS_CREATE_RELEASE_KEY);
	}

	if (hasObjectKeys(data, ['planId', 'isReleaseEnabled'])) {
		return getAnthropicKeyFromLegacyKey(JIRA_PLANS_MANAGE_FEATURES_KEY);
	}

	if (hasObjectKeys(data, ['planId', 'deleteIssueSourceId'])) {
		return getAnthropicKeyFromLegacyKey(JIRA_PLANS_UPDATE_WORK_SOURCE_KEY);
	}

	if (
		hasObjectKeys(data, ['planId', 'title', 'type']) &&
		(hasObjectKeys(data, ['projectId']) || hasObjectKeys(data, ['rowId']))
	) {
		return getAnthropicKeyFromLegacyKey(JIRA_PLANS_UPDATE_WORK_SOURCE_KEY);
	}

	return undefined;
};

const getJiraPlansCustomUiActionKey = (action: MessageAction): string | undefined => {
	if (isJiraPlansAnthropicActionKey(action.key)) {
		return action.key;
	}

	const toolName = getToolName(action);
	if (toolName && isJiraPlansAnthropicActionKey(toolName)) {
		return toolName;
	}

	if (fg('rovo_chat_jira_plans_anthropic_migration')) {
		const anthropicKeyFromPayload = getJiraPlansAnthropicKeyFromPayload(action);
		if (anthropicKeyFromPayload) {
			return anthropicKeyFromPayload;
		}

		const anthropicKeyFromLegacyKey = getAnthropicKeyFromLegacyKey(action.key);
		if (anthropicKeyFromLegacyKey) {
			return anthropicKeyFromLegacyKey;
		}
	}

	return isJiraPlansLegacyActionKey(action.key) ? action.key : undefined;
};

const normalizeJiraPlansCustomUiAction = (
	action: MessageAction,
): MessageAction | JiraPlansNormalizedMessageAction => {
	const customUiActionKey = getJiraPlansCustomUiActionKey(action);
	if (!customUiActionKey || customUiActionKey === action.key) {
		return action;
	}

	return {
		...action,
		key: customUiActionKey,
		jiraPlansOriginalActionKey: action.key,
	};
};

const normalizeJiraPlansCustomUiActions = (actions: MessageAction[]): MessageAction[] =>
	fg('rovo_chat_jira_plans_anthropic_migration')
		? actions.map(normalizeJiraPlansCustomUiAction)
		: actions;

const JIRA_PLANS_ACTION_KEYS_TO_NOT_RENDER_LLM_CONTENT: ReadonlySet<string> = new Set([
	JIRA_PLANS_ADD_DEPENDENCY_KEY,
	JIRA_PLANS_ADD_PROGRAM_BOARD_KEY,
	JIRA_PLANS_CREATE_RELEASE_KEY,
	JIRA_PLANS_CREATE_WORK_ITEM_KEY,
	JIRA_PLANS_DELETE_PROGRAM_BOARD_KEY,
	JIRA_PLANS_MANAGE_FEATURES_KEY,
	JIRA_PLANS_REMOVE_DEPENDENCY_KEY,
	JIRA_PLANS_UPDATE_WORK_SOURCE_KEY,
	...Object.values(JIRA_PLANS_LEGACY_KEY_TO_ANTHROPIC_MIGRATION_KEY),
]);

const shouldNotRenderLLMContentForJiraPlansAction = (action: MessageAction): boolean => {
	const jiraPlansCustomUiActionKey = getJiraPlansCustomUiActionKey(action);
	return Boolean(
		jiraPlansCustomUiActionKey &&
		JIRA_PLANS_ACTION_KEYS_TO_NOT_RENDER_LLM_CONTENT.has(jiraPlansCustomUiActionKey),
	);
};

/**
 * The Anthropic version of the Jira Plans skills delivers the tool arguments
 * wrapped in `{ tool_name, input }` instead of passing the flat payload directly
 * as `action.data`. Unwrap `input` so the existing plan renderers keep reading
 * their typed payload fields off `data`.
 *
 * Falls back to treating `data` as the payload itself, so this is safe for both
 * the legacy (flat) and new (wrapped) shapes regardless of which one is flowing.
 */
const getPlanActionData = <T>(data: unknown): T => {
	const parsedData = parseActionData(data);
	const isWrappedPayload =
		parsedData !== null &&
		typeof parsedData === 'object' &&
		'input' in parsedData &&
		(parsedData as AnthropicToolPayload).input !== null &&
		typeof (parsedData as AnthropicToolPayload).input === 'object';

	if (isWrappedPayload) {
		return (parsedData as { input: T }).input;
	}
	return parsedData as T;
};

/**
 * Re-wraps a (possibly modified) plan payload back into the Anthropic
 * `{ tool_name, input }` envelope when the original `data` used that shape, so
 * confirm/actioned callbacks forward the same contract the backend sent. When
 * the original `data` was the legacy flat payload, the payload is returned as-is.
 */
const withPlanActionData = (originalData: unknown, payload: unknown): unknown => {
	const parsedOriginalData = parseActionData(originalData);
	if (
		parsedOriginalData !== null &&
		typeof parsedOriginalData === 'object' &&
		'input' in parsedOriginalData
	) {
		const wrappedPayload = { ...(parsedOriginalData as object), input: payload };
		return typeof originalData === 'string' ? JSON.stringify(wrappedPayload) : wrappedPayload;
	}
	return payload;
};

const isPlanActionKeyForRenderer = (actionKey: string, legacyActionKey: string): boolean => {
	if (resolveJiraPlansAnthropicActionKey(actionKey) === legacyActionKey) {
		return fg('rovo_chat_jira_plans_anthropic_migration');
	}

	if (actionKey === legacyActionKey) {
		return !fg('rovo_chat_jira_plans_anthropic_migration');
	}

	return false;
};

export const jiraPlansActionData: JiraPlansActionData = {
	isCustomUiActionKey: isJiraPlansCustomUiActionKey,
	isLegacyActionKey: isJiraPlansLegacyActionKey,
	isAnthropicActionKey: isJiraPlansAnthropicActionKey,
	resolveAnthropicActionKey: resolveJiraPlansAnthropicActionKey,
	getCustomUiActionKey: getJiraPlansCustomUiActionKey,
	normalizeCustomUiActions: normalizeJiraPlansCustomUiActions,
	shouldNotRenderLLMContentForAction: shouldNotRenderLLMContentForJiraPlansAction,
	getPlanActionData,
	withPlanActionData,
	isPlanActionKeyForRenderer,
};
